import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectFilteredGames,
  selectFilters,
  setFilter,
  clearFilters,
  setSearchQuery,
} from '../store/slices/gamesSlice';
import { Badge } from '../components/ui';
import { PlatformIcon, SearchIcon, FilterIcon } from '../components/common/Icons';
import { platformsData } from '../data/platforms';

const getStatusVariant = (status) => {
  const variants = {
    'Available Now': 'available',
    'Coming Soon': 'coming-soon',
    'Beta': 'beta',
    'Early Access': 'early-access',
    'Announced': 'announced',
    'In Development': 'in-development',
  };
  return variants[status] || 'default';
};

const GameCard = ({ game }) => {
  return (
    <Link
      to={`/games/${game.slug}`}
      className="card-hover group rounded-3xl overflow-hidden bg-white/5 border border-white/10 block"
    >
      <div className="aspect-video relative" style={{ background: game.image }}>
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
        
        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <Badge variant={getStatusVariant(game.status)} size="sm">
            {game.status}
          </Badge>
        </div>

        {/* Platforms */}
        <div className="absolute bottom-4 left-4 flex gap-2">
          {game.platforms.slice(0, 3).map((p) => (
            <div
              key={p}
              className="w-8 h-8 rounded-lg bg-black/50 backdrop-blur flex items-center justify-center text-white/80"
            >
              <PlatformIcon platform={p} className="w-4 h-4" />
            </div>
          ))}
          {game.platforms.length > 3 && (
            <div className="w-8 h-8 rounded-lg bg-black/50 backdrop-blur flex items-center justify-center text-white/80 text-xs font-bold">
              +{game.platforms.length - 3}
            </div>
          )}
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="px-6 py-3 bg-primary-500 rounded-full font-bold text-sm">
            VIEW GAME
          </span>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h3 className="font-gaming text-xl font-bold group-hover:text-primary-500 transition-colors">
            {game.title}
          </h3>
          {game.rating && (
            <div className="flex items-center gap-1 text-yellow-400">
              <span>★</span>
              <span className="text-sm">{game.rating}</span>
            </div>
          )}
        </div>
        <p className="text-gray-400 text-sm mb-3">{game.tagline}</p>
        <div className="flex flex-wrap gap-2">
          {game.genres.slice(0, 3).map((genre) => (
            <Badge key={genre} variant="default" size="sm">
              {genre}
            </Badge>
          ))}
        </div>
      </div>
    </Link>
  );
};

const GamesPage = () => {
  const dispatch = useDispatch();
  const filteredGames = useSelector(selectFilteredGames);
  const filters = useSelector(selectFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const platform = searchParams.get('platform');
    const genre = searchParams.get('genre');
    const status = searchParams.get('status');
    const search = searchParams.get('search');
    if (!platform && !genre && !status && !search) return;
    dispatch(clearFilters());
    if (platform) dispatch(setFilter({ filterType: 'platform', value: platform }));
    if (genre) dispatch(setFilter({ filterType: 'genre', value: genre }));
    if (status) dispatch(setFilter({ filterType: 'status', value: status }));
    if (search) dispatch(setSearchQuery(search));
  }, [searchParams, dispatch]);

  const allGenres = useMemo(() => {
    const genres = new Set();
    filteredGames.forEach((game) => game.genres.forEach((g) => genres.add(g)));
    return Array.from(genres).sort();
  }, []);

  const allStatuses = [
    'Available Now',
    'Coming Soon',
    'In Development',
    'Beta',
    'Early Access',
    'Announced',
  ];

  const handleFilterChange = (filterType, value) => {
    dispatch(setFilter({ filterType, value }));
  };

  const handleSearchChange = (e) => {
    dispatch(setSearchQuery(e.target.value));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const activeFiltersCount = Object.values(filters).filter(
    (v) => v !== 'all' && v !== ''
  ).length;

  return (
    <div className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="section-subtitle text-primary-500">OUR COLLECTION</p>
          <h1 className="section-title">
            ALL <span className="text-gradient">GAMES</span>
          </h1>
          <p className="section-description">
            Explore our complete collection of African-inspired gaming experiences
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                type="text"
                placeholder="Search games..."
                value={filters.search}
                onChange={handleSearchChange}
                className="input-field pl-12"
              />
            </div>

            {/* Filter Toggle (Mobile) */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden btn-secondary flex items-center justify-center gap-2"
            >
              <FilterIcon className="w-5 h-5" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge variant="primary" size="sm">
                  {activeFiltersCount}
                </Badge>
              )}
            </button>
          </div>

          {/* Filter Options */}
          <div
            className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${
              showFilters ? 'block' : 'hidden md:grid'
            }`}
          >
            {/* Platform Filter */}
            <select
              value={filters.platform}
              onChange={(e) => handleFilterChange('platform', e.target.value)}
              className="input-field"
            >
              <option value="all">All Platforms</option>
              {platformsData.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>

            {/* Genre Filter */}
            <select
              value={filters.genre}
              onChange={(e) => handleFilterChange('genre', e.target.value)}
              className="input-field"
            >
              <option value="all">All Genres</option>
              {allGenres.map((genre) => (
                <option key={genre} value={genre}>
                  {genre}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="input-field"
            >
              <option value="all">All Status</option>
              {allStatuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="btn-ghost text-primary-500"
              >
                Clear Filters ({activeFiltersCount})
              </button>
            )}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-gray-400">
          Showing {filteredGames.length} game{filteredGames.length !== 1 ? 's' : ''}
        </div>

        {/* Games Grid */}
        {filteredGames.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredGames.map((game) => (
              <GameCard key={game.id} game={game} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🎮</div>
            <h3 className="text-2xl font-bold mb-2">No games found</h3>
            <p className="text-gray-400 mb-6">
              Try adjusting your filters or search terms
            </p>
            <button onClick={handleClearFilters} className="btn-primary">
              Clear All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GamesPage;
